import { createReducer, on } from '@ngrx/store';
import { produce } from 'immer';
import moment from 'moment';
import { Reservation, ReservationListFilters, ReservationPurpose, Shift, SortType } from 'src/app/model/reservation';
import * as ReservationListActions from '../actions/reservation-list.actions';
import * as ReservationActions from '../actions/reservation.actions';
import { TablesPlanActions } from '@store/actions/tables-plan.actions';
import { Common } from 'src/app/lib/common-methods';

export const reservationListFeatureKey = 'reservationList';

export interface State {
  reservations: Reservation[];
  cachedReservationsLists: { date: Date, reservations: Reservation[] }[]; // keep a copy for use on offline mode
  date: Date;
  editingReservationId: string | null;
  shifts: Shift[];
  processedShifts: Shift[];
  blockingInterval: string;
  blockingPurpose: ReservationPurpose;
  blockingCategory: string;
  pendingReservations: Reservation[];
  refreshListInterval: number;
  expandedReservationIds: string[];
  globalSearchTerm: string;
  sortType: SortType;
  sortDesc: boolean;
  selectedShift: Shift | null | undefined;
  filters: ReservationListFilters;
  compactView: boolean;
  rerervationIdForOrderDetails: string | null;
  scrollTop: number;
}

export const initialState: State = {
  reservations: [],
  cachedReservationsLists: [],
  date: new Date(),
  editingReservationId: null,
  shifts: [],
  processedShifts: [],
  blockingInterval: '30',
  blockingPurpose: 'All',
  blockingCategory: 'All',
  pendingReservations: [],
  refreshListInterval: 15000,
  expandedReservationIds: [],
  globalSearchTerm: '',
  sortType: SortType.Time,
  sortDesc: false,
  selectedShift: null,
  filters: {
    groupByStatus: false,
    withDietary: false,
    withType: false,
    withResvTags: false,
    withNotes: false,
    withDiscount: false,
    withBill: false,
    withCake: false,
    withSource: false,
    withFood: false,
    withDrink: false,
    withTokenization: false,
    //withDeposit: false
    reservationCategories: [],
  },
  compactView: true,
  rerervationIdForOrderDetails: null,
  scrollTop: 0,
};

export const reducer = createReducer(
  initialState,

  on(ReservationListActions.loadReservations, state => state),
  on(ReservationListActions.loadReservationsSuccess, (state, action) => {
    return produce(state, (draftState) => {
      draftState.reservations = action.reservations;

      const sameDatePendingReservations = draftState.pendingReservations.filter((reservation) => {
        return reservationIsOnWorkingDate(reservation.reservationDetails.arrivalTime, state.date);
      });

      // replace with the pending reservations if any
      for (const pendingReservation of sameDatePendingReservations) {
        const index = draftState.reservations
          .findIndex(reservation => (pendingReservation.reservationDetails.reservationId != null
            && reservation.reservationDetails.reservationId === pendingReservation.reservationDetails.reservationId)
            || (pendingReservation.reservationDetails.tmpId != null
              && reservation.reservationDetails.tmpId === pendingReservation.reservationDetails.tmpId));

        if (index >= 0) {
          draftState.reservations = Object.assign([], draftState.reservations, { [index]: pendingReservation });
        } else {
          draftState.reservations = [...draftState.reservations, ...[pendingReservation]];
        }
      }

      const moment1 = moment(draftState.date, 'MM/D/YYYY');
      const moment2 = moment(new Date(), 'MM/D/YYYY');

      const listToCache = { date: draftState.date, reservations: draftState.reservations };

      if (moment1.isSame(moment2, 'date')) {
        // day is today, replace the today entry or add it if it doesn't exist
        const index = draftState.cachedReservationsLists.findIndex(list => {
          const listMoment = moment(list.date);
          return listMoment.isSame(moment1, 'date');
        });
        if (index >= 0) {
          // replace it
          draftState.cachedReservationsLists = Object.assign([], draftState.cachedReservationsLists, { [index]: listToCache });
        } else {
          draftState.cachedReservationsLists.push(listToCache);
        }
      } else {
        // day is not today

        // remove any list which is not today
        draftState.cachedReservationsLists = draftState.cachedReservationsLists.filter(list => {
          const moment3 = moment(list.date);
          return moment3.isSame(moment2, 'date');
        });

        // add the new list to cache
        draftState.cachedReservationsLists.push(listToCache);
      }

      draftState.shifts = action.shifts;
    });
  }),
  on(ReservationListActions.loadReservationsFailure, (state, action) => {
    return produce(state, (draftState) => {

      if (state.cachedReservationsLists.length > 0) {

        const cachedList = draftState.cachedReservationsLists.find(list => {
          const moment1 = moment(list.date);
          const moment2 = moment(state.date, 'MM/D/YYYY');

          console.log(moment1.toDate(), moment2.toDate());

          return moment1.isSame(moment2, 'date');
        });

        if (cachedList != null) {
          draftState.reservations = cachedList.reservations;
        } else {
          draftState.reservations = [];
        }
      } else {
        draftState.reservations = [];
      }

      const sameDatePendingReservations = draftState.pendingReservations.filter((reservation) => {
        return reservationIsOnWorkingDate(reservation.reservationDetails.arrivalTime, state.date);
      });

      // replace with the pending reservations if any
      for (const pendingReservation of sameDatePendingReservations) {
        const index = draftState.reservations
          .findIndex(reservation => (pendingReservation.reservationDetails.reservationId != null
            && reservation.reservationDetails.reservationId === pendingReservation.reservationDetails.reservationId)
            || (pendingReservation.reservationDetails.tmpId != null
              && reservation.reservationDetails.tmpId === pendingReservation.reservationDetails.tmpId));

        if (index >= 0) {
          draftState.reservations = Object.assign([], draftState.reservations, { [index]: pendingReservation });
        } else {
          draftState.reservations = [...draftState.reservations, ...[pendingReservation]];
        }
      }

    });
  }),
  on(ReservationListActions.setDate, (state, action) => {
    if (isNaN(new Date(action.date).getDate())) {
      return state;
    }

    const date = moment(action.date).toDate();

    return produce(state, (draftState) => {
      draftState.date = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        12,
        0,
        0,
        0
      );
    });
  }),
  on(ReservationListActions.setEditingReservationId, (state, action) => {
    return produce(state, (draftState) => {
      draftState.editingReservationId = action.reservationId;
    });
  }),
  on(ReservationListActions.updateReservation, (state, action) => {

    const selectedReservation = state.reservations
      .find(reservation => (reservation.reservationDetails.reservationId != null && reservation.reservationDetails.reservationId === state.editingReservationId)
        || (reservation.reservationDetails.tmpId != null && reservation.reservationDetails.tmpId === state.editingReservationId));

    if (selectedReservation == null) {
      return state;
    }

    const index = state.reservations.indexOf(selectedReservation);
    const reservation = action.reservation;

    return produce(state, (draftState) => {
      draftState.reservations = Object.assign([], state.reservations, { [index]: reservation });
    });
  }),
  on(ReservationListActions.setBlockingInterval, (state, action) => {
    return produce(state, (draftState) => {
      draftState.blockingInterval = action.blockingInterval;
    });
  }),
  on(ReservationListActions.setBlockingPurpose, (state, action) => {
    return produce(state, (draftState) => {
      draftState.blockingPurpose = action.blockingPurpose;
    });
  }),
  on(ReservationListActions.setBlockingCategory, (state, action) => {
    return produce(state, (draftState) => {
      draftState.blockingCategory = action.blockingCategory;
    });
  }),
  on(ReservationActions.submitReservationSuccess, (state, action) => {

    const reservation = action.reservation;

    if (reservation?.reservationDetails?.reservationId == null) {
      return state;
    }

    const index = state.reservations.findIndex(r => r.reservationDetails.reservationId === reservation.reservationDetails.reservationId);

    if (index < 0) {
      return state;
    }

    return produce(state, (draftState) => {
      draftState.reservations = Object.assign([], draftState.reservations, { [index]: reservation });
    });
  }),
  on(ReservationListActions.setProcessedShifts, (state, action) => {
    return produce(state, (draftState) => {
      draftState.processedShifts = action.processedShifts;
    });
  }),
  on(ReservationListActions.addPendingReservation, (state, action) => {
    return produce(state, (draftState) => {

      const index = state.pendingReservations.findIndex(reservation => (action.reservation.reservationDetails.reservationId != null
        && reservation.reservationDetails.reservationId === action.reservation.reservationDetails.reservationId)
        || (action.reservation.reservationDetails.tmpId != null
          && reservation.reservationDetails.tmpId === action.reservation.reservationDetails.tmpId));

      if (index >= 0) {
        // already in pending - replace
        draftState.pendingReservations = Object.assign([], draftState.pendingReservations, { [index]: action.reservation });
      } else {
        draftState.pendingReservations = [...draftState.pendingReservations, ...[action.reservation]];
      }

      // add newly created pending reservations to the reservations list if the reservation's date matches list's date
      const reservationsListIndex = draftState.reservations
        .findIndex(reservation => (reservation.reservationDetails.tmpId != null && reservation.reservationDetails.tmpId === action.reservation.reservationDetails.tmpId)
          || reservation.reservationDetails.reservationId === action.reservation.reservationDetails.reservationId);

      if (reservationsListIndex < 0) {
        const isSameDate = reservationIsOnWorkingDate(action.reservation.reservationDetails.arrivalTime, state.date);
        if (isSameDate) {
          draftState.reservations = [...draftState.reservations, ...[action.reservation]];
        }
      } else {
        draftState.reservations = Object.assign([], draftState.reservations, { [reservationsListIndex]: action.reservation });
      }

    });
  }),
  on(ReservationListActions.setPendingReservations, (state, action) => {
    return produce(state, (draftState) => {
      draftState.pendingReservations = action.reservations;
    });
  }),
  on(ReservationListActions.submitPendingReservationSuccess, (state, action) => {
    return produce(state, (draftState) => {

      const index = state.pendingReservations.indexOf(action.reservation);

      if (index < 0) {
        return;
      }

      draftState.pendingReservations.splice(index, 1);
    });
  }),
  on(ReservationListActions.expandReservation, (state, action) => {
    return produce(state, (draftState) => {
      draftState.expandedReservationIds.push(action.reservationId);
    });
  }),
  on(ReservationListActions.collapseReservation, (state, action) => {
    return produce(state, (draftState) => {
      draftState.expandedReservationIds = draftState.expandedReservationIds.filter(reservationId => reservationId !== action.reservationId);
    });
  }),
  on(ReservationListActions.setGlobalSearchTerm, (state, action) => {
    return produce(state, (draftState) => {
      draftState.globalSearchTerm = action.term;
    });
  }),
  on(ReservationListActions.setSortType, (state, action) => {
    return produce(state, (draftState) => {
      draftState.sortType = action.sortType;
    });
  }),
  on(ReservationListActions.setSortDesc, (state, action) => {
    return produce(state, (draftState) => {
      draftState.sortDesc = action.sortDesc;
    });
  }),
  on(ReservationListActions.setFilters, (state, action) => {
    return produce(state, (draftState) => {
      draftState.filters = action.filters;
    });
  }),
  on(ReservationListActions.setSelectedShift, (state, action) => {
    return produce(state, (draftState) => {
      draftState.selectedShift = action.shift;
    });
  }),
  on(ReservationListActions.setCachedReservationsList, (state, action) => {
    return produce(state, (draftState) => {
      draftState.cachedReservationsLists = action.lists ?? [];
    });
  }),
  on(ReservationListActions.clearCachedReservationsList, (state, action) => {
    return produce(state, (draftState) => {
      draftState.cachedReservationsLists = [];
    });
  }),
  on(ReservationListActions.setCompactView, (state, action) => {
    return produce(state, (draftState) => {
      draftState.compactView = action.value;
    });
  }),
  on(ReservationListActions.loadListFiltersAndSorting, (state, action) => {
    return produce(state, (draftState) => {
      draftState.filters = action.filters;
      draftState.sortType = action.sortType;
      draftState.sortDesc = action.sortDesc;
      draftState.selectedShift = action.selectedShift;
    });
  }),

  // setReservationAllocatedTables //todo: move to Reservations reducer
  on(ReservationListActions.setReservationAllocatedTables, (state, action) => {
    return produce(state, (draftState) => {

      if (action.reservation?.reservationDetails == null) {
        return;
      }

      const reservation = draftState.reservations.find(r => r.reservationDetails.reservationId === action.reservation.reservationDetails.reservationId);

      if (reservation == null) {
        return;
      }

      const allocatedTables = action.tables ?? [];
      
      reservation.reservationDetails.allocatedTables = allocatedTables.map(t => t.tableName).join(',');
    });
  }),

  // setReservationAllocatedTablesAndSubmit
  on(TablesPlanActions.setReservationAllocatedTablesAndSubmit, (state, action) => {
    return produce(state, (draftState) => {
      
      if (action.reservation?.reservationDetails?.reservationId == null) {
        return;
      }

      const reservation = draftState.reservations.find(r => r.reservationDetails.reservationId === action.reservation.reservationDetails.reservationId);

      if (reservation?.reservationDetails == null) {
        return;
      }  
      
      reservation.reservationDetails.allocatedTables = action.allocatedTables.join(',');
    });
  }),
  
  on(ReservationListActions.setOrderDetailsReservation, (state, action) => {
    return produce(state, (draftState) => {
      draftState.rerervationIdForOrderDetails = action.reservationId;
    });
  }),

  // setScrollTop
  on(ReservationListActions.setScrollTop, (state, action) => {
    return produce(state, (draftState) => {
      draftState.scrollTop = action.scrollTop;
    });
  }),
);

const reservationIsOnWorkingDate = (arrivalTime: string, workingDate: Date): boolean => {

  const reservationTime = moment(arrivalTime).toDate();

  const reservationHours = reservationTime.getHours();
  const isTimeNextDayMorning = reservationHours < Common.WORKING_DAY_START_HOUR;

  if (isTimeNextDayMorning) {
    reservationTime.setDate(reservationTime.getDate() - 1);
  }

  const moment1 = moment(reservationTime);
  const moment2 = moment(workingDate);

  return moment1.isSame(moment2, 'date');
}
